import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Alert, AlertSeverity } from '../../api/alerts';
import { useAlerts } from '../../hooks/useAlerts';
import './Alerts.scss';

const ICON: Record<AlertSeverity, IconProp> = {
    Error: 'circle-xmark',
    Warning: 'triangle-exclamation',
    Info: 'circle-info',
};

const when = (iso: string): string =>
    new Date(iso).toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

/**
 * The bell in the header: how many alerts are new, and the list behind a click, with the
 * way to switch on notifications of the browser itself.
 */
export const AlertBell = () => {
    const { alerts, unread, live, notifications, markSeen, acknowledge, enableNotifications, open } = useAlerts();
    const [expanded, setExpanded] = useState<boolean>(false);
    const [showDone, setShowDone] = useState<boolean>(false);
    const panel = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!expanded) {
            return;
        }

        const close = (event: MouseEvent) => {
            if (panel.current && !panel.current.contains(event.target as Node)) {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', close);

        return () => document.removeEventListener('mousedown', close);
    }, [expanded]);

    const toggle = () => {
        if (!expanded) {
            markSeen();
        }

        setExpanded(!expanded);
    };

    const shown = alerts.filter((a) => showDone || !a.acknowledgedAt).slice(0, 50);

    return (
        <div className="alert-bell" ref={panel}>
            <button
                type="button"
                className="alert-bell__button"
                onClick={toggle}
                aria-label={`Meldingen${unread > 0 ? `, ${unread} nieuw` : ''}`}
                title={live ? 'Meldingen (live)' : 'Meldingen (niet live verbonden; wordt elke minuut ververst)'}
            >
                <FontAwesomeIcon icon="bell" />
                {unread > 0 && <span className="alert-bell__count">{unread > 99 ? '99+' : unread}</span>}
            </button>

            {expanded && (
                <div className="alert-bell__panel" role="dialog" aria-label="Meldingen">
                    <div className="alert-bell__head">
                        <strong>Meldingen</strong>
                        <label className="alert-bell__toggle">
                            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
                            ook afgehandeld
                        </label>
                    </div>

                    {notifications === 'default' && (
                        <button type="button" className="btn btn-sm btn-outline-primary alert-bell__enable" onClick={enableNotifications}>
                            <FontAwesomeIcon icon="bell" /> Browsermeldingen aanzetten
                        </button>
                    )}
                    {notifications === 'denied' && (
                        <p className="alert-bell__muted">
                            Browsermeldingen staan uit voor deze site; zet ze aan in de instellingen van de browser.
                        </p>
                    )}

                    {shown.length === 0 ? (
                        <p className="alert-bell__muted">Geen meldingen.</p>
                    ) : (
                        <ul className="alert-bell__list">
                            {shown.map((alert) => (
                                <AlertRow
                                    key={alert.id}
                                    alert={alert}
                                    onOpen={() => {
                                        open(alert);
                                        setExpanded(false);
                                    }}
                                    onAcknowledge={() => acknowledge([alert.id])}
                                />
                            ))}
                        </ul>
                    )}

                    {shown.some((a) => !a.acknowledgedAt) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => acknowledge(shown.filter((a) => !a.acknowledgedAt).map((a) => a.id))}
                        >
                            <FontAwesomeIcon icon="check" /> Alles afgehandeld
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const AlertRow = ({ alert, onOpen, onAcknowledge }: { alert: Alert; onOpen: () => void; onAcknowledge: () => void }) => (
    <li className={`alert-row alert-row--${alert.severity.toLowerCase()}${alert.acknowledgedAt ? ' alert-row--done' : ''}`}>
        <FontAwesomeIcon icon={ICON[alert.severity] ?? 'circle-info'} className="alert-row__icon" />
        <div className="alert-row__text">
            <button type="button" className="alert-row__title" onClick={onOpen} disabled={!alert.link}>
                {alert.title}
            </button>
            {alert.message && <span className="alert-row__message">{alert.message}</span>}
            <span className="alert-row__meta">
                {when(alert.createdAt)}
                {alert.acknowledgedAt && ` · afgehandeld${alert.acknowledgedBy ? ` door ${alert.acknowledgedBy}` : ''}`}
            </span>
        </div>
        {!alert.acknowledgedAt && (
            <button type="button" className="alert-row__ack" onClick={onAcknowledge} title="Afgehandeld" aria-label="Afgehandeld">
                <FontAwesomeIcon icon="check" />
            </button>
        )}
    </li>
);

/**
 * New alerts as toasts in the corner, on whatever page is open.
 */
export const AlertToasts = () => {
    const { toasts, dismissToast, open } = useAlerts();

    if (toasts.length === 0) {
        return null;
    }

    return (
        <div className="alert-toasts" role="status" aria-live="polite">
            {toasts.map((alert) => (
                <div key={alert.id} className={`alert-toast alert-toast--${alert.severity.toLowerCase()}`}>
                    <FontAwesomeIcon icon={ICON[alert.severity] ?? 'circle-info'} />
                    <div className="alert-toast__text">
                        <button type="button" className="alert-toast__title" onClick={() => open(alert)}>
                            {alert.title}
                        </button>
                        {alert.message && <span>{alert.message}</span>}
                    </div>
                    <button type="button" className="alert-toast__close" onClick={() => dismissToast(alert.id)} aria-label="Sluiten">
                        <FontAwesomeIcon icon="xmark" />
                    </button>
                </div>
            ))}
        </div>
    );
};
